import { Flex, DisplaySwitch, FlexProps, Card } from '@blockanalitica/ui';
import { useMemo, useState } from 'react';
import AtRiskChart from './AtRiskChart';
import AtRiskHistoryChart from './AtRiskHistoryChart';
import AtRiskPerDropChart from './AtRiskPerDropChart';
import { ChartTimeFilter } from '@/components';

interface AtRiskTabsProps extends FlexProps {
  marketAddress: string;
  tag: string;
  symbol: string;
}

export default function AtRiskTabs({
  marketAddress,
  tag,
  symbol,
  ...rest
}: AtRiskTabsProps) {
  const [activeDisplayOption, setActiveDisplayOption] = useState('at-risk');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');

  const displayOptions = useMemo(
    () => [
      { key: 'at-risk', value: 'At Risk' },
      { key: 'at-risk-historic', value: 'At Risk - Historic' },
      { key: 'at-risk-per-drop', value: 'At Risk Per Drop' }
    ],
    []
  );

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const content = useMemo(() => {
    switch (activeDisplayOption) {
      case 'at-risk':
        return (
          <AtRiskChart
            marketAddress={marketAddress}
            tag={tag}
            symbol={symbol}
          />
        );
      case 'at-risk-historic':
        return (
          <AtRiskHistoryChart
            marketAddress={marketAddress}
            chartDaysAgo={chartDaysAgo}
            tag={tag}
            symbol={symbol}
          />
        );
      case 'at-risk-per-drop':
        return (
          <AtRiskPerDropChart
            marketAddress={marketAddress}
            tag={tag}
            symbol={symbol}
          />
        );
      default:
        return null;
    }
  }, [activeDisplayOption, marketAddress, chartDaysAgo, tag, symbol]);

  return (
    <Card flexDirection="column" gap="xs" {...rest}>
      <Flex
        justifyContent="space-between"
        alignItems={['center', 'flex-start']}
        flexDirection={['column', 'row']}
        gap={['xs', 'm']}>
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        {activeDisplayOption === 'at-risk-historic' ? timeFilter : null}
      </Flex>
      {content}
    </Card>
  );
}
